import React, { useState, useRef, useEffect } from "react";
import { BaseAppProps } from "../../../types/webos";

// Import music files as URLs
import awesomeMixVol2Url from "url:./music/awesome-mix-vol2.mp3";

interface Track {
  id: string;
  title: string;
  artist: string;
  url: string;
}

// Fixed playlist
const playlist: Track[] = [
  {
    id: "awesome-mix-vol2",
    title: "Awesome Mix Vol 2",
    artist: "Guardians of the Galaxy",
    url: awesomeMixVol2Url,
  },
];

interface OscilloscopeProps {
  isPlaying: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

function Oscilloscope({ isPlaying, audioRef }: OscilloscopeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    let dataArray: Uint8Array;
    let bufferLength: number;

    const setupAudioContext = () => {
      if (!audioRef.current || audioContextRef.current) return;

      try {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        sourceRef.current = audioContextRef.current.createMediaElementSource(
          audioRef.current
        );

        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);

        analyserRef.current.fftSize = 256;
        bufferLength = analyserRef.current.frequencyBinCount;
        dataArray = new Uint8Array(new ArrayBuffer(bufferLength));
      } catch (error) {
        console.warn("Could not setup audio context:", error);
      }
    };

    const draw = () => {
      if (!ctx || !canvas) return;

      ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (isPlaying && analyserRef.current && dataArray) {
        analyserRef.current.getByteFrequencyData(dataArray as any);

        const barWidth = (canvas.width / bufferLength) * 2.5;
        let barHeight;
        let x = 0;

        // Draw frequency bars
        for (let i = 0; i < bufferLength; i++) {
          barHeight = (dataArray[i] / 255) * canvas.height * 0.8;

          const hue = (i / bufferLength) * 360;
          ctx.fillStyle = `hsla(${hue}, 70%, 60%, 0.8)`;
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

          x += barWidth + 1;
        }
      } else {
        // Static bars when not playing
        const time = Date.now() * 0.001;
        const barWidth = (canvas.width / 64) * 2.5;
        let x = 0;

        for (let i = 0; i < 64; i++) {
          const barHeight =
            (Math.sin(time + i * 0.1) * 0.5 + 0.5) * canvas.height * 0.3;
          const hue = (i / 64) * 360;
          ctx.fillStyle = `hsla(${hue}, 30%, 30%, 0.3)`;
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
          x += barWidth + 1;
        }
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    if (isPlaying && audioRef.current) {
      setupAudioContext();
    }

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, audioRef]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-60"
      style={{ background: "linear-gradient(45deg, #1a1a2e, #16213e)" }}
    />
  );
}

export function App({ onClose }: BaseAppProps) {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const currentTrack = playlist[currentTrackIndex];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      nextTrack();
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentTrackIndex]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch((error) => {
        console.warn("Could not play audio:", error);
      });
      setIsPlaying(true);
    }
  };

  const nextTrack = () => {
    const nextIndex = (currentTrackIndex + 1) % playlist.length;
    setCurrentTrackIndex(nextIndex);
    setIsPlaying(false);
  };

  const previousTrack = () => {
    const prevIndex =
      currentTrackIndex === 0 ? playlist.length - 1 : currentTrackIndex - 1;
    setCurrentTrackIndex(prevIndex);
    setIsPlaying(false);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="h-full relative overflow-hidden">
      {/* Oscilloscope Background */}
      <Oscilloscope isPlaying={isPlaying} audioRef={audioRef} />

      {/* Audio Element */}
      <audio ref={audioRef} src={currentTrack.url} preload="metadata" />

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8 text-white">
        {/* Track Info */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2 text-shadow">
            {currentTrack.title}
          </h1>
          <p className="text-xl opacity-80 text-shadow">
            {currentTrack.artist}
          </p>
          <p className="text-sm opacity-60 mt-2">
            Track {currentTrackIndex + 1} of {playlist.length}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full max-w-md mb-8">
          <div className="flex justify-between text-sm opacity-80 mb-2">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-8">
          {/* Previous Button */}
          <button
            onClick={previousTrack}
            className="p-4 rounded-full bg-gray-800 bg-opacity-50 hover:bg-opacity-70 transition-all duration-200 transform hover:scale-110"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
            </svg>
          </button>

          {/* Play/Pause Button */}
          <button
            onClick={togglePlayPause}
            className="p-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-110 shadow-lg"
          >
            {isPlaying ? (
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Next Button */}
          <button
            onClick={nextTrack}
            className="p-4 rounded-full bg-gray-800 bg-opacity-50 hover:bg-opacity-70 transition-all duration-200 transform hover:scale-110"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Custom CSS for text shadow */}
      <style>{`
        .text-shadow {
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
        }
      `}</style>
    </div>
  );
}
